<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — EVA-M5, extended in EVA-M7 (7.3).
 * The FAQ review tab: lists the candidates Eva mined from resolved conversations (held out of search
 * until approved), labelled by source (conversation / document / manual). An agent can bulk-approve
 * the vetted ones, edit a candidate's question/answer in place (re-embeds), or reject it.
 * Tailwind-only; Composition API.
 */
import { reactive, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import EvaResponsesAPI from 'dashboard/api/eva/responses';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const responses = ref([]);
const selectedIds = ref([]);
const isLoading = ref(false);
const isApproving = ref(false);
const isSaving = ref(false);
const hasError = ref(false);
const editingId = ref(null);
const editDraft = reactive({ question: '', answer: '' });

const hasPending = computed(() => responses.value.length > 0);
const canApprove = computed(
  () => selectedIds.value.length > 0 && !isApproving.value
);

const sourceLabels = computed(() => ({
  manual: t('EVA_FAQ_APPROVAL.SOURCE_MANUAL'),
  document: t('EVA_FAQ_APPROVAL.SOURCE_DOCUMENT'),
  conversation: t('EVA_FAQ_APPROVAL.SOURCE_CONVERSATION'),
}));

const loadPending = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await EvaResponsesAPI.getResponses({
      assistantId: props.assistantId,
      status: 'pending',
    });
    responses.value = data;
    selectedIds.value = [];
  } catch {
    // surface a load failure instead of rendering it as an empty list (and swallowing the rejection)
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const toggle = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(selected => selected !== id)
    : [...selectedIds.value, id];
};

const approveSelected = async () => {
  if (!canApprove.value) return;
  isApproving.value = true;
  try {
    await EvaResponsesAPI.bulkApprove({
      assistantId: props.assistantId,
      ids: selectedIds.value,
    });
    await loadPending();
  } catch {
    hasError.value = true;
  } finally {
    isApproving.value = false;
  }
};

const startEdit = response => {
  editingId.value = response.id;
  Object.assign(editDraft, {
    question: response.question,
    answer: response.answer,
  });
};

const cancelEdit = () => {
  editingId.value = null;
};

const saveEdit = async id => {
  if (!editDraft.question.trim() || !editDraft.answer.trim()) return;
  isSaving.value = true;
  hasError.value = false;
  try {
    await EvaResponsesAPI.updateResponse({
      id,
      question: editDraft.question.trim(),
      answer: editDraft.answer.trim(),
    });
    editingId.value = null;
    await loadPending();
  } catch {
    hasError.value = true;
  } finally {
    isSaving.value = false;
  }
};

const reject = async id => {
  try {
    await EvaResponsesAPI.delete(id);
  } catch {
    hasError.value = true;
  } finally {
    await loadPending();
  }
};

onMounted(loadPending);

defineExpose({
  loadPending,
  approveSelected,
  startEdit,
  cancelEdit,
  saveEdit,
  reject,
  editDraft,
});
</script>

<template>
  <div class="flex flex-col gap-4 min-w-0">
    <header class="flex items-center justify-between gap-3">
      <h3 class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
        {{ t('EVA_FAQ_APPROVAL.TITLE') }}
        <span
          v-if="hasPending"
          class="inline-flex px-1.5 py-0.5 text-xs rounded-full tabular-nums bg-n-alpha-black2 text-n-slate-11"
        >
          {{ responses.length }}
        </span>
      </h3>
      <Button
        sm
        :label="t('EVA_FAQ_APPROVAL.APPROVE_SELECTED')"
        :is-loading="isApproving"
        :disabled="!canApprove"
        @click="approveSelected"
      />
    </header>

    <p v-if="hasError" class="text-sm text-n-ruby-11">
      {{ t('EVA_FAQ_APPROVAL.ERROR') }}
    </p>
    <p v-else-if="isLoading" class="text-sm text-n-slate-11">
      {{ t('EVA_FAQ_APPROVAL.LOADING') }}
    </p>
    <p
      v-else-if="!hasPending"
      class="px-6 py-6 text-sm text-center border border-dashed rounded-xl border-n-weak text-n-slate-11"
    >
      {{ t('EVA_FAQ_APPROVAL.EMPTY') }}
    </p>

    <ul
      v-else
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container divide-y divide-n-weak"
    >
      <li
        v-for="response in responses"
        :key="response.id"
        class="flex flex-col gap-3 p-4 min-w-0"
      >
        <!-- edit form -->
        <template v-if="editingId === response.id">
          <Input
            v-model="editDraft.question"
            :label="t('EVA_FAQ_APPROVAL.QUESTION')"
          />
          <TextArea
            v-model="editDraft.answer"
            auto-height
            min-height="5rem"
            max-height="16rem"
            :label="t('EVA_FAQ_APPROVAL.ANSWER')"
          />
          <div class="flex justify-end gap-2">
            <Button
              faded
              slate
              sm
              :label="t('EVA_FAQ_APPROVAL.CANCEL')"
              @click="cancelEdit"
            />
            <Button
              sm
              :label="t('EVA_FAQ_APPROVAL.SAVE')"
              :is-loading="isSaving"
              :disabled="
                isSaving ||
                !editDraft.question.trim() ||
                !editDraft.answer.trim()
              "
              @click="saveEdit(response.id)"
            />
          </div>
        </template>

        <!-- static row -->
        <div v-else class="flex items-start justify-between gap-3">
          <label class="flex gap-3 min-w-0 cursor-pointer">
            <input
              type="checkbox"
              class="mt-1 shrink-0"
              :checked="selectedIds.includes(response.id)"
              @change="toggle(response.id)"
            />
            <span class="flex flex-col gap-1 min-w-0">
              <span
                class="self-start px-1.5 py-0.5 text-xs rounded-md text-n-slate-11 bg-n-alpha-black2"
              >
                {{ sourceLabels[response.source] }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{ response.question }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ response.answer }}
              </span>
            </span>
          </label>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              icon="i-lucide-pen"
              slate
              xs
              ghost
              :aria-label="t('EVA_FAQ_APPROVAL.EDIT')"
              @click="startEdit(response)"
            />
            <span class="w-px h-4 bg-n-weak" />
            <Button
              ghost
              ruby
              xs
              :label="t('EVA_FAQ_APPROVAL.REJECT')"
              @click="reject(response.id)"
            />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
